import classNames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'react-jss';
import { ContextualMenuItemType, DirectionalHint } from '@fluentui/react';
import { contains, without, isEmpty, noop, mapObject, constant, filter, chain, map } from 'underscore';
import { Glyphicon, Button, OverlayTrigger, Tooltip } from '@bingads-webui-react/primitive';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { PredicateUIType, convertFilterEntityToPredicates, extendPredicatesWithSelectorConfig, FilterBarIngoreClickOutsideClassName } from '@bingads-webui/filter-commons';
import { FilterI18n } from '@bingads-webui/filter-i18n';
import { SearchBox } from '@bingads-webui-react/search-box-control';
import { SearchResultLiveRegion } from '@bingads-webui-react/search-box';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ContextualMenu, TooltipHost } from '@bingads-webui-react/fabric-control';
import { HumanizedPredicate } from '@bingads-webui/humanized-predicate';

import { FilterRemoveModal } from './filter-remove-modal';
import { getStyles } from './getStyles';
import { poPPattern, phoneBiPattern, biPattern, customColumnPattern, conversionAttributes, competitiveAttributes } from './consts';

const CommonCalloutProps = ({ classes }) => ({
  // ensure scroll bar click will not close filter bar
  className: classNames(FilterBarIngoreClickOutsideClassName, classes.rootSubCallout),
  preventDismissOnScroll: true,
});

class UnthemedFilterMenu extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      query: '',
    };
    this.i18nConverter = new FilterI18n(this.props.filterSchema, this.props.i18n, null, null, true);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && !this.props.show) {
      this.clearQuery();
    }
  }

  onClickRemove = (savedFilter) => {
    this.setState({
      removeDialog: this.getFilterRemovalDialog(savedFilter),
    });

    this.props.onDismiss();
  };

  onClickProperty = (property) => {
    this.props.showPredicatePopover(property);
  };

  onQueryChange = (query) => {
    this.setState({
      query,
    });
  };

  onSearch = () => {
    const { query } = this.state;

    if (!query) {
      return;
    }

    const savedFilters = this.getMatchedSavedFilters();

    if (savedFilters.length > 0) {
      this.props.onApplySavedFilter(savedFilters[0]);
      return;
    }

    const matchedProperties = this.getMatchedProperties();

    if (matchedProperties.length > 0) {
      this.props.showPredicatePopover(matchedProperties[0]);
    }
  };

  onQueryAbort = () => {
    this.clearQuery();
  };

  getMatchedSavedFilters() {
    const { query } = this.state;
    const { savedFilters } = this.props;

    if (query.trim()) {
      return savedFilters.filter(savedFilter =>
        !query || this.matchQuery(savedFilter).matched)
        .sort((savedFilter1, savedFilter2) => savedFilter1.length - savedFilter2.length);
    }

    return savedFilters;
  }

  getMatchedProperties() {
    const keys = this.props.filterSchema.getPropertyKeys();

    return keys.filter((key) => {
      if (contains(this.props.readOnlyProperties, key)
      || contains(this.props.disallowedProperties, key)) {
        return false;
      }
      if (!this.matchQuery(this.i18nConverter.convertName(key)).matched) {
        return false;
      }

      const { isPoPEnabled } = this.props;

      if (!isPoPEnabled) {
        const propertyPath = this.getPropertyPath(key);

        return !poPPattern.test(propertyPath);
      }
      return true;
    }).sort((key1, key2) => {
      const name1 = this.i18nConverter.convertName(key1);
      const name2 = this.i18nConverter.convertName(key2);
      return name1.length - name2.length;
    });
  }

  getFilterRemovalDialog = savedFilter => (
    <FilterRemoveModal
      classeName={
        classNames(
          'filter-remove-modal',
          FilterBarIngoreClickOutsideClassName,
          this.props.classes.filterRemoveModel
        )
      }
      removePromptText={this.props.i18n.getString(_TL_('Are you sure you want to remove the saved item: {0}')).replace('{0}', savedFilter)}
      removeText={this.props.i18n.getString(_TL_('Remove'))}
      cancelText={this.props.i18n.getString(_TL_('Cancel'))}
      title={this.props.i18n.getString(_TL_('Remove saved filter'))}
      closeButtonAriaLabel={this.props.i18n.getString(_TL_('Close'))}
      onCancel={() => {
        this.setState({
          removeDialog: null,
        });
      }}
      onRemove={() => {
        this.setState({
          removeDialog: null,
        });
        notificationApi.showInfo(this.props.i18n.getString(_TL_('Your filter has been removed.')));
        this.props.onDeleteFilters(savedFilter);
      }}
    />);

  getPropertyPath(key) {
    return this.props.filterSchema.entitySchema.properties[key].property ||
        this.props.filterSchema.entitySchema.properties[key].field;
  }

  getCategories(keys) {
    const callDetails = { name: 'CallDetails', keys: [], text: this.props.i18n.getString(_TL_('Call details')) };
    const competitive = { name: 'Competitive', keys: [], text: this.props.i18n.getString(_TL_('Competitive')) };
    const conversions = { name: 'Conversions', keys: [], text: this.props.i18n.getString(_TL_('Conversions')) };
    const performance = { name: 'Performance', keys: [], text: this.props.i18n.getString(_TL_('Performance')) };
    const customColumns = { name: 'CustomColumns', keys: [], text: this.props.i18n.getString(_TL_('Custom Columns')) };
    const attributes = {
      name: 'Attributes', keys: [], text: this.props.i18n.getString(_TL_('Attributes')), className: this.props.classes.scrolledDopDown,
    };

    keys.forEach((key) => {
      if (contains(this.props.readOnlyProperties, key)
      || contains(this.props.disallowedProperties, key)
      || this.props.filterSchema.entitySchema.properties[key].isDeletedProperty) {
        return;
      }

      const propertyPath = this.getPropertyPath(key);

      if (phoneBiPattern.test(propertyPath) && propertyPath !== 'PhonePerformanceMetrics/ClickAndPhoneSpend') {
        callDetails.keys.push(key);
      } else if (customColumnPattern.test(key)) {
        customColumns.keys.push(key);
      } else if (biPattern.test(propertyPath) || propertyPath === 'PhonePerformanceMetrics/ClickAndPhoneSpend') {
        if (contains(conversionAttributes, key) || contains(conversionAttributes, `PerformanceMetrics${key}`)) {
          conversions.keys.push(key);
        } else if (contains(competitiveAttributes, key) || contains(competitiveAttributes, `PerformanceMetrics${key}`)) {
          competitive.keys.push(key);
        } else {
          performance.keys.push(key);
        }
      } else if (!poPPattern.test(propertyPath)) {
        attributes.keys.push(key);
      }
    });

    return {
      callDetails,
      competitive,
      conversions,
      performance,
      attributes,
      customColumns,
    };
  }

  matchQuery = (text) => {
    const query = this.state.query.toLowerCase().trim();
    const index = text.toLowerCase().indexOf(query);

    return {
      matched: index > -1,
      index,
      length: query.length,
    };
  };

  clearQuery() {
    this.setState({
      query: '',
    });
  }

  generateSavedFilters() {
    const filters = this.getMatchedSavedFilters();

    if (filters.length === 0) {
      return null;
    }

    const menuItem = {
      key: 'savedFilters',
      subMenuProps: {
        calloutProps: CommonCalloutProps({ classes: this.props.classes }),
      },
      text: this.props.i18n.getString(_TL_('Saved filters')),
      className: 'saved-filter-menu',
    };

    const savedFiltersProps = filters.map(savedFilter => ({
      key: savedFilter,
      text: savedFilter,
      onRender: () => this.renderSavedFilters(savedFilter),
    }));

    const { query } = this.state;

    // Plain item under search scenario.
    if (query.trim()) {
      return [{
        key: 'Saved-filters',
        itemType: ContextualMenuItemType.Header,
        text: this.props.i18n.getString(_TL_('Saved filters')),
        className: this.props.classes.header,
      }].concat(savedFiltersProps);
    }

    menuItem.subMenuProps.items = savedFiltersProps;

    return [menuItem];
  }

  generateRecentFilters() {
    const filters = this.props.recentFilters;

    if (filters.length === 0) {
      return null;
    }

    let recentFiltersProps = [{
      key: 'Recent filters',
      itemType: ContextualMenuItemType.Header,
      text: this.props.i18n.getString(_TL_('Recent filters')),
      className: this.props.classes.header,
    }];

    recentFiltersProps = recentFiltersProps.concat(filters.map((recentFilter, index) => ({
      key: index,
      text: index,
      onRender: () => this.renderRecentFilter(recentFilter, index),
    })));

    return recentFiltersProps;
  }

  generateProperties() {
    const { query } = this.state;

    if (query.trim()) {
      const matchedProperties = this.getMatchedProperties();
      if (matchedProperties.length === 0) {
        return null;
      }

      return matchedProperties.map(this.generateOneItem);
    }

    const keys = this.props.filterSchema.getPropertyKeys();
    const categories = this.getCategories(keys);
    const categoriesCount = filter(categories, category => !isEmpty(category.keys)).length;

    if (categoriesCount === 1) {
      return chain(keys)
        .filter(key => !contains(this.props.readOnlyProperties, key)
        && !contains(this.props.disallowedProperties, key))
        .map(this.generateOneItem)
        .value();
    }

    const props = [
      this.generateOneCategory(categories.attributes),
      this.generateOneCategory(categories.performance),
      this.generateOneCategory(categories.conversions),
      this.generateOneCategory(categories.competitive),
      this.generateOneCategory(categories.callDetails),
      this.generateOneCategory(categories.customColumns),
    ];

    return without(props, null);
  }

  generateOneCategory({
    name, keys, text,
  }) {
    if (keys.length === 0) {
      return null;
    }

    const { isPoPEnabled } = this.props;

    const menuItemProps = {
      key: name,
      subMenuProps: {
        calloutProps: CommonCalloutProps({ classes: this.props.classes }),
      },
      text,
      className: 'category',
    };

    const items = isPoPEnabled ? this.generateItemsOrPopMenu(keys) : keys.map(this.generateOneItem);

    menuItemProps.subMenuProps.items = items;

    return menuItemProps;
  }

  generateCustomMenuItem(item, isDisabled) {
    const { classes } = this.props;

    return (
      <Button
        onClick={() => {
          if (item.onClick) {
            item.onClick();
          }
        }}
        disabled={isDisabled}
        key={item.text}
        role="menuitem"
        className={classes.textButton}
        btnStyle="link"
      >
        {this.renderText(item.text, classes.textDiv, classes.textSpan)}
      </Button>
    );
  }

  generateOneItem = (key) => {
    const { isInvalidProperty } = this.props.filterSchema.entitySchema.properties[key];

    if (isInvalidProperty) {
      const { i18n } = this.props;

      return ({
        onRender: item => (
          <TooltipHost content={i18n.getString(_TL_('This filter is not available in this page. Please select a different filter.'))}>
            {this.generateCustomMenuItem(item, true)}
          </TooltipHost>
        ),
        key,
        text: this.i18nConverter.convertName(key),
        onClick: () => {},
        className: 'property',
      });
    }

    return ({
      key,
      text: this.i18nConverter.convertName(key),
      onClick: () => this.onClickProperty(key),
      className: 'property',
    });
  };

  generateItemsOrPopMenu(keys) {
    return keys.map((key) => {
      const propertyPath = this.getPropertyPath(key);

      if ((phoneBiPattern.test(propertyPath) || biPattern.test(propertyPath))
        && this.props.filterSchema.entitySchema.properties[`PoP${key}`]
        && !this.props.filterSchema.entitySchema.properties[key].isInvalidProperty
      ) {
        const menuItemProps = {
          key: `${key}-pop`,
          subMenuProps: {
            calloutProps: CommonCalloutProps({ classes: this.props.classes }),
          },
          text: this.i18nConverter.convertName(key),
          className: 'category',
        };

        const items = [key, `PoP${key}`, `PoP${key}Change`, `PoP${key}ChangePercent`].map(this.generateOneItem);

        menuItemProps.subMenuProps.items = items;

        return menuItemProps;
      }

      return this.generateOneItem(key);
    });
  }

  renderRecentFilter = (recentFilter, recentFilterIndex) => {
    const {
      classes, onApplyRecentFilter, i18n, i18nConverter,
      getPredicateIconClassName, predicateValueNameGetter,
      onDismiss,
    } = this.props;

    const selectablePredicates =
    extendPredicatesWithSelectorConfig(this.props.selector, convertFilterEntityToPredicates(
      recentFilter.quickFilter,
      PredicateUIType.Selector
    ));
    const editablePredicates =
      convertFilterEntityToPredicates(recentFilter.editableFilter, PredicateUIType.Editor);
    const allPredicates = selectablePredicates.concat(editablePredicates);
    const text = map(allPredicates, (predicate, index) => (
      <React.Fragment>
        <HumanizedPredicate
          predicate={predicate}
          i18nConverter={i18nConverter}
          i18n={i18n}
          getPredicateIconClassName={getPredicateIconClassName}
          predicateValueNameGetter={predicateValueNameGetter}
          textOnly
        />
        {index !== allPredicates.length - 1 ? '; ' : ''}
      </React.Fragment>
    ));
    return (
      <div
        className={classNames('recent-filter-container', classes.recentFilterItem)}
        key={`${recentFilter} container`}
      >
        <OverlayTrigger
          key="collapse-button-tooptip"
          placement="right"
          delayShow={300}
          overlay={
            <Tooltip id="tooltip-right">
              {text}
            </Tooltip>
          }
        >
          <Button
            key={recentFilter}
            onClick={() => {
              onApplyRecentFilter(recentFilterIndex);
              onDismiss();
            }}
            className={classNames('recent-filter-button', classes.textButton)}
            btnStyle="link"
            role="menuitem"
          >
            <div className={classNames(classes.recentFilterText)}>
              <span>
                {text}
              </span>
            </div>
          </Button>
        </OverlayTrigger>
      </div>
    );
  };

  renderSavedFilters = (savedFilter) => {
    const { classes, onApplySavedFilter, i18n } = this.props;

    return (
      <div
        className={classNames('saved-filter-container', classes.savedFilterItem)}
        key={`${savedFilter} container`}
      >
        <Button
          key={savedFilter}
          onClick={() => onApplySavedFilter(savedFilter)}
          className={classNames('saved-filter-text', classes.textButton)}
          btnStyle="link"
          role="menuitem"
        >
          {this.renderText(savedFilter, classes.savedFilterText, classes.textSpan)}
        </Button>
        <Button
          onClick={() => this.onClickRemove(savedFilter)}
          className={classNames('saved-filter-remove', classes.savedFilterRemove, classes.textButton, classes.removeButton)}
          key={`${savedFilter} remove`}
          btnStyle="link"
          aria-label={i18n.getString(_TL_('Remove this saved filter'))}
        >
          <Glyphicon glyph="ba-Cancel" />
        </Button>
      </div>
    );
  };

  renderMenuList = (menuListProps, defaultRender) => {
    const { query } = this.state;
    const { classes, i18n } = this.props;
    const totalCount = menuListProps.totalItemCount === 1 && menuListProps.items[0].key === 'no-results' ? 0 : menuListProps.totalItemCount;

    return (
      <div className={classes.filterMenu}>
        <ThemeProvider theme={{
          components: {
            searchBox: {
              inputForListBox: {
                width: 0,
                flex: '1 1 auto',
              },
            },
          },
        }}
        >
          <SearchBox
            isForListBox
            onQueryChange={this.onQueryChange}
            onSearch={this.onSearch}
            placeholder={i18n.getString(_TL_('Search'))}
            value={query || ''}
            onDestroy={this.onQueryAbort}
            ariaLabel={i18n.getString(_TL_('Search filter menu'))}
            arrowIconAriaLabel={i18n.getString(_TL_('Search'))}
          />
        </ThemeProvider>
        <SearchResultLiveRegion
          totalCount={totalCount}
          emptyText={curlyTemplate(i18n.getString(_TL_('No Results found for {{searchText}}')))({
            searchText: query || '',
          })}
          resultText={curlyTemplate(i18n.getString(_TL_('Found {{totalCount}} Results for {{searchText}}')))({
            totalCount,
            searchText: query || '',
          })}
        />
        {defaultRender(menuListProps)}
      </div>
    );
  };

  renderText = (text, textDiv, textSpan) => {
    const { query } = this.state;

    let getDecoratedText = () => (
      <span className={textSpan}>{text}</span>
    );

    if (query) {
      const { matched, index, length } = this.matchQuery(text);

      if (matched) {
        getDecoratedText = () => (
          <span className={textSpan}>
            {text.substring(0, index)}
            <strong>
              {text.substring(index, index + length)}
            </strong>
            {text.substring(index + length)}
          </span>
        );
      }
    }

    return (
      <div className={textDiv}>
        {getDecoratedText()}
      </div>
    );
  };

  render() {
    const { query, removeDialog } = this.state;

    let items = [];

    const recentFiltersProps = this.generateRecentFilters();
    const savedFiltersProps = this.generateSavedFilters();
    const propertiesProps = this.generateProperties();

    if (recentFiltersProps) {
      items = items.concat(recentFiltersProps);

      if (savedFiltersProps || propertiesProps) {
        items = items.concat([{
          key: 'divider_1',
          itemType: ContextualMenuItemType.Divider,
        }]);
      }
    }

    if (savedFiltersProps) {
      items = items.concat(savedFiltersProps);

      if (propertiesProps) {
        items = items.concat([{
          key: 'divider_2',
          itemType: ContextualMenuItemType.Divider,
        }]);
      }
    }

    if (propertiesProps) {
      items = items.concat(propertiesProps);
    }

    if (isEmpty(items)) {
      items.push({
        key: 'no-results',
        onRender: () => {},
      });
    } else if (query) {
      items = items.map(menuItem => (
        {
          onRender: item => this.generateCustomMenuItem(item),
          ...menuItem,
        }
      ));
    }

    return (
      <React.Fragment>
        <ContextualMenu
          items={items}
          hidden={!this.props.show}
          target={this.props.toggleRef}
          onRenderMenuList={this.renderMenuList}
          calloutProps={{
            className:
              classNames(FilterBarIngoreClickOutsideClassName, this.props.classes.rootCallout),
            preventDismissOnScroll: true,
            calloutMaxHeight: 362,
          }}
          shouldFocusOnMount
          onDismiss={this.props.onDismiss}
          className="contextual-filter-menu"
          directionalHint={DirectionalHint.bottomLeftEdge}
        />
        {removeDialog}
      </React.Fragment>
    );
  }
}

UnthemedFilterMenu.propTypes = {
  show: PropTypes.bool,
  onDeleteFilters: PropTypes.func.isRequired,
  onApplySavedFilter: PropTypes.func,
  onApplyRecentFilter: PropTypes.func,
  showPredicatePopover: PropTypes.func.isRequired,
  savedFilters: PropTypes.arrayOf(PropTypes.shape()),
  recentFilters: PropTypes.arrayOf(PropTypes.shape()),
  filterSchema: PropTypes.shape({
    entitySchema: PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      properties: PropTypes.arrayOf(PropTypes.shape()),
    }),
    getFilterProperty: PropTypes.func,
    getPropertyKeys: PropTypes.func.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isPoPEnabled: PropTypes.bool,
  classes: PropTypes.shape(mapObject(
    getStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  toggleRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  onDismiss: PropTypes.func,
  readOnlyProperties: PropTypes.arrayOf(PropTypes.string),
  disallowedProperties: PropTypes.arrayOf(PropTypes.string),
  i18nConverter: PropTypes.shape().isRequired,
  getPredicateIconClassName: PropTypes.func,
  predicateValueNameGetter: PropTypes.func,
  selector: PropTypes.arrayOf(PropTypes.shape({
    property: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.arrayOf(PropTypes.string),
    })),
    removable: PropTypes.bool,
    defaultValue: PropTypes.arrayOf(PropTypes.string),
  })),
};

UnthemedFilterMenu.defaultProps = {
  show: false,
  isPoPEnabled: false,
  savedFilters: [],
  recentFilters: [],
  onApplySavedFilter: noop,
  onApplyRecentFilter: noop,
  toggleRef: null,
  onDismiss: noop,
  readOnlyProperties: [],
  disallowedProperties: [],
  getPredicateIconClassName: undefined,
  predicateValueNameGetter: undefined,
  selector: [],
};

export const FilterMenuReact = withDefaultStyles(UnthemedFilterMenu, getStyles);
